import { Box, RadioGroup, VStack } from '@chakra-ui/react';
import { Radio } from 'library/atoms';
import { useEffect, useState } from 'react';
import { AppTracker } from 'shared/analytics/tracker';
import { ANALYTIC_V3_EVENTS } from 'views/main/organization/analytics-v3/constants';
import {
  useGetAnalyticsCountByServiceExport,
  useGetAnalyticsCountByServiceGroupByPriorityExport,
  useGetAnalyticsCountVsTimeExport,
  useGetAnalyticsCountVsTimeByStatusExport,
  useGetAnalyticsCountVsTimeByPriorityExport,
} from 'views/main/organization/analytics-v3/hooks/useGetAnalyticsData';
import PanelCard from '../../../PanelCard';
import IncidentCountByAssignee from './IncidentCountByAssignee';
import IncidentCountByPriority from './IncidentCountByPriority';
import IncidentCountByService from './IncidentCountByService';
import IncidentCountByServiceGroupByPriority from './IncidentCountByServiceGroupByPriority';
import IncidentCountByStatus from './IncidentCountByStatus';
import IncidentCountByTeam from './IncidentCountByTeam';
import IncidentCountOverTimeByPriority from './IncidentCountOverTimeByPriority';
import IncidentCountOverTimeByStatus from './IncidentCountOverTimeByStatus';
import { IncidentCountVsTime } from './IncidentCountVsTime';
import { DashboardContainer } from './style';

function Dashboard({ isOrgAnalytics = false }: { isOrgAnalytics?: boolean }) {
  const [selectedBarChart, setSelectedBarChart] = useState<
    'all' | 'grouped-by-status' | 'grouped-by-priority'
  >('all');

  const [selectedCountByServiceChart, setSelectedCountByServiceChart] = useState<
    'grouped-by-priority' | 'all'
  >('all');

  const { isLoading: isLoadingCountVsTime, refetch: refetchCountVsTime } =
    useGetAnalyticsCountVsTimeExport({ isOrgAnalytics });

  const { isLoading: isLoadingCountVsTimeByStatus, refetch: refetchCountVsTimeByStatus } =
    useGetAnalyticsCountVsTimeByStatusExport({ isOrgAnalytics });

  const { isLoading: isLoadingCountVsTimeByPriority, refetch: refetchCountVsTimeByPriority } =
    useGetAnalyticsCountVsTimeByPriorityExport({ isOrgAnalytics });

  const handleExportCountVsTime = () => {
    if (selectedBarChart === 'all') {
      refetchCountVsTime();
      AppTracker.track(ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED, {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Incident Analytics',
        'Chart Title': 'Incident Count Over Time',
      });
    } else if (selectedBarChart === 'grouped-by-status') {
      refetchCountVsTimeByStatus();
      AppTracker.track(ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED, {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Incident Analytics',
        'Chart Title': 'Incident Count Over Time',
      });
    } else if (selectedBarChart === 'grouped-by-priority') {
      AppTracker.track(ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED, {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Incident Analytics',
        'Chart Title': 'Incident Count Over Time',
      });
      refetchCountVsTimeByPriority();
    }
  };

  const { isLoading: isLoadingCountByService, refetch: refetchCountByService } =
    useGetAnalyticsCountByServiceExport();

  const {
    isLoading: isLoadingCountByServiceGroupByPriority,
    refetch: refetchCountByServiceGroupByPriority,
  } = useGetAnalyticsCountByServiceGroupByPriorityExport();

  const handleExportCountByService = () => {
    if (selectedCountByServiceChart === 'all') {
      AppTracker.track(ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED, {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Incident Analytics',
        'Chart Title': 'Incident Count by Service',
      });
      refetchCountByService();
    } else if (selectedCountByServiceChart === 'grouped-by-priority') {
      AppTracker.track(ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_EXPORT_CLICKED, {
        'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
        'Analytics Type': 'Incident Analytics',
        'Analytics Tab': 'Incident Analytics',
        'Chart Title': 'Incident Count by Service Grouped by Priority',
      });
      refetchCountByServiceGroupByPriority();
    }
  };

  useEffect(() => {
    AppTracker.track(ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_PAGE_VIEWED, {
      'Analytics Scope': isOrgAnalytics ? 'Organization Analytics' : 'Team Analytics',
      'Analytics Type': 'Incident Analytics',
      'Analytics Tab': 'Incident Analytics',
    });
  }, []);

  return (
    <DashboardContainer>
      <div className="row">
        <IncidentCountByStatus isOrgAnalytics={isOrgAnalytics} />
        <IncidentCountByPriority isOrgAnalytics={isOrgAnalytics} />
      </div>

      {isOrgAnalytics && (
        <div className="row">
          <IncidentCountByTeam isOrgAnalytics={isOrgAnalytics} />
        </div>
      )}

      <div className="row">
        <PanelCard title="Incident Count Over Time" onExportClick={handleExportCountVsTime}>
          <div>
            <Box position="relative" height={282}>
              {selectedBarChart === 'all' && (
                <IncidentCountVsTime isOrgAnalytics={isOrgAnalytics} />
              )}
              {selectedBarChart === 'grouped-by-status' && (
                <IncidentCountOverTimeByStatus isOrgAnalytics={isOrgAnalytics} />
              )}
              {selectedBarChart === 'grouped-by-priority' && (
                <IncidentCountOverTimeByPriority isOrgAnalytics={isOrgAnalytics} />
              )}
            </Box>
            <VStack alignItems="center" marginTop={4}>
              <RadioGroup
                value={selectedBarChart}
                onChange={value => {
                  AppTracker.track(
                    ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_GROUP_BY_APPLIED,
                    {
                      'Analytics Scope': isOrgAnalytics
                        ? 'Organization Analytics'
                        : 'Team Analytics',
                      'Analytics Type': 'Incident Analytics',
                      'Analytics Tab': 'Incident Analytics',
                      'Chart Title': 'Incident Count Over Time',
                      'Group By Field': value,
                    },
                    true,
                  );
                  setSelectedBarChart(value as 'all' | 'grouped-by-status' | 'grouped-by-priority');
                }}
                display="flex"
                columnGap={6}
              >
                <Box py={1.5} px={2} border="1px solid #E1E6EB" display="flex" alignItems="center">
                  <Radio value="all" label="All" />
                </Box>
                <Box py={1.5} px={2} border="1px solid #E1E6EB" display="flex" alignItems="center">
                  <Radio value="grouped-by-status" label="Group by Status" />
                </Box>
                <Box py={1.5} px={2} border="1px solid #E1E6EB" display="flex" alignItems="center">
                  <Radio value="grouped-by-priority" label="Group by Priority" />
                </Box>
              </RadioGroup>
            </VStack>
          </div>
        </PanelCard>
      </div>

      {!isOrgAnalytics && (
        <div className="row">
          <IncidentCountByAssignee isOrgAnalytics={isOrgAnalytics} />
          <PanelCard
            title="Incident Count by Service "
            onExportClick={handleExportCountByService}
            topElement={
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <div className="panel-card-body-chart-desc">Top 10 Services</div>
                <VStack alignItems="center">
                  <RadioGroup
                    value={selectedCountByServiceChart}
                    onChange={value => {
                      AppTracker.track(
                        ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_CHART_GROUP_BY_APPLIED,
                        {
                          'Analytics Scope': isOrgAnalytics
                            ? 'Organization Analytics'
                            : 'Team Analytics',
                          'Analytics Type': 'Incident Analytics',
                          'Analytics Tab': 'Incident Analytics',
                          'Chart Title': 'Incident Count by Service',
                          'Group By Field': value,
                        },
                        true,
                      );
                      setSelectedCountByServiceChart(value as 'all' | 'grouped-by-priority');
                    }}
                    display="flex"
                    columnGap={6}
                  >
                    <Box
                      py={1.5}
                      px={2}
                      border="1px solid #E1E6EB"
                      display="flex"
                      alignItems="center"
                    >
                      <Radio value="all" label="All" />
                    </Box>
                    <Box
                      py={1.5}
                      px={2}
                      border="1px solid #E1E6EB"
                      display="flex"
                      alignItems="center"
                    >
                      <Radio value="grouped-by-priority" label="Group by Priority" />
                    </Box>
                  </RadioGroup>
                </VStack>
              </Box>
            }
          >
            <Box position="relative" height={616}>
              {selectedCountByServiceChart === 'all' && (
                <IncidentCountByService isOrgAnalytics={isOrgAnalytics} />
              )}
              {selectedCountByServiceChart === 'grouped-by-priority' && (
                <IncidentCountByServiceGroupByPriority isOrgAnalytics={isOrgAnalytics} />
              )}
            </Box>
          </PanelCard>
        </div>
      )}
    </DashboardContainer>
  );
}

export default Dashboard;
